import { Command } from 'cmdk'
import { FC, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import './cmdk.css'
import { useDebouncedState } from '@mantine/hooks'
import { useAtomValue } from 'jotai'
import { usePostHog } from 'posthog-js/react'
import { useNavigate } from 'react-router-dom'
import { useAdminScopes, usePrefetchAdminScopes } from '~/api/admin/admin-scope.api'
import { useAllAdminSites } from '~/api/admin/admin-site.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { Icon, IconName } from '~/components/Icon'
import { buildSections } from '~/components/layout/menu/menu.config'
import { Skeleton } from '~/components/Skeleton'
import { useIsSparkDemandDashboardEnabled } from '~/hooks/useSparkDemand'
import { useIsSparkForPubUiEnabled } from '~/hooks/useSparkForPub'
import { useSwitchScope } from '~/hooks/useSwitchScope'
import { currentScopeState } from '~/state'
import { useCanAccessRoute } from '~/utils/router'
import { copyToClipboard } from '~/utils/string'

// Recent items management
const RECENT_KEY = 'pubstack-command-palette-recent-v2'
const MAX_RECENT = 7

type BaseRecentItem = {
  id: string
  label: string
  timestamp: number
}

type NavigationRecentItem = BaseRecentItem & {
  type: 'navigation'
  path: string
  section: string
}

type ActionRecentItem = BaseRecentItem & {
  type: 'action'
  actionId: string
}

type RecentItem = NavigationRecentItem | ActionRecentItem

type RecentItemInput = Omit<NavigationRecentItem, 'timestamp'> | Omit<ActionRecentItem, 'timestamp'>

const getRecentItems = (): RecentItem[] => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
  } catch {
    return []
  }
}

// External store (localStorage) for recent items, read via useSyncExternalStore.
// The snapshot is cached so it keeps a stable reference between actual changes,
// as required by React (returning a fresh array every call would loop).
let recentItemsSnapshot: RecentItem[] = getRecentItems()
const recentItemsListeners = new Set<() => void>()
const refreshRecentItems = () => {
  recentItemsSnapshot = getRecentItems()
  recentItemsListeners.forEach((listener) => listener())
}
window.addEventListener('storage', refreshRecentItems)
const subscribeRecentItems = (listener: () => void) => {
  recentItemsListeners.add(listener)
  return () => recentItemsListeners.delete(listener)
}
const getRecentItemsSnapshot = () => recentItemsSnapshot

const saveRecentItem = (item: RecentItemInput) => {
  const recent = getRecentItems()
  const updated = [{ ...item, timestamp: Date.now() }, ...recent.filter((r) => r.id !== item.id)].slice(0, MAX_RECENT)
  localStorage.setItem(RECENT_KEY, JSON.stringify(updated))
  refreshRecentItems()
}

// Section icons mapping
const SECTION_ICONS: Record<string, IconName> = {
  Analytics: 'chart_bar',
  'Ad Management': 'ad_stack',
  Settings: 'settings',
  Admin: 'admin',
  Recent: 'time',
}

// Action icons mapping (for recent items display)
const ACTION_ICONS: Record<string, IconName> = {
  'search-site': 'site',
  'search-scope': 'folder',
  'copy-scope-id': 'bolt',
  'use-clickhouse': 'bolt',
  'use-opensearch': 'bolt',
}

// Page display names for breadcrumb
const PAGE_NAMES: Record<string, string> = {
  searchSite: 'Sites',
  searchScopes: 'Scopes',
}

const SEARCH_PLACEHOLDERS = {
  searchSite: 'Type a name, or a site id',
  searchScopes: 'Search by scope name or id',
  undefined: 'Type to search',
}
export const CommandMenu = () => {
  const [open, setOpen] = useState(false)
  const posthog = usePostHog()
  const navigate = useNavigate()
  const canAccessRoute = useCanAccessRoute()
  const user = useAuthenticatedUser()
  const currentScope = useAtomValue(currentScopeState)
  const [search, setSearch] = useState<string | undefined>('')
  // Un seul state pour la recherche de site avec useDebouncedState
  const [siteSearch, setSiteSearch] = useDebouncedState('', 300)
  const sparkForPubUiEnabled = useIsSparkForPubUiEnabled()
  const sparkDemandDashboardEnabled = useIsSparkDemandDashboardEnabled()
  const rawSections = useMemo(() => buildSections({ alertsNotification: 0, sparkForPubUiEnabled, sparkDemandDashboardEnabled }), [sparkForPubUiEnabled, sparkDemandDashboardEnabled])
  const [pages, setPages] = useState<('searchSite' | 'searchScopes')[]>([])
  const page = pages[pages.length - 1]
  const recentItems = useSyncExternalStore(subscribeRecentItems, getRecentItemsSnapshot)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Warm the admin lambda (cold start) and prefetch scopes as soon as the palette opens, so the
  // first access to scope/site search is instant instead of paying the cold start on first click.
  const prefetchAdminScopes = usePrefetchAdminScopes()
  useEffect(() => {
    if (open && user.isAdmin) prefetchAdminScopes(true)
  }, [open, user.isAdmin])

  // Track navigation and save to recents
  const trackAndNavigate = useCallback(
    (path: string, label: string, section: string) => {
      saveRecentItem({ type: 'navigation', id: path, label, path, section })
      navigate(path)
      setOpen(false)
    },
    [navigate]
  )

  // Track action and save to recents
  const trackAction = useCallback((actionId: string, label: string, callback: () => void) => {
    saveRecentItem({ type: 'action', id: actionId, label, actionId })
    callback()
  }, [])

  // Handle click on recent items (navigation or action)
  const handleRecentItemClick = useCallback(
    (item: RecentItem) => {
      if (item.type === 'navigation') {
        navigate(item.path)
        setOpen(false)
        return
      }

      // Actions
      switch (item.actionId) {
        case 'search-site':
          setPages(['searchSite'])
          setSearch('')
          break
        case 'search-scope':
          setPages(['searchScopes'])
          setSearch('')
          break
        case 'copy-scope-id':
          copyToClipboard(currentScope.id)
          setOpen(false)
          break
      }
    },
    [navigate, currentScope.id]
  )

  const admin = useMemo(() => {
    if (!user.isAdmin) return []
    return [
      {
        heading: 'Admin',
        id: 'admin',
        items: [
          {
            children: 'Manage all scopes',
            type: 'Navigation' as const,
            to: '/admin/scopes',
            onSelect: () => trackAndNavigate('/admin/scopes', 'Manage all scopes', 'Admin'),
          },
          {
            children: 'Manage this scope',
            type: 'Navigation' as const,
            to: `/admin/scopes/${currentScope.id}`,
            onSelect: () => trackAndNavigate(`/admin/scopes/${currentScope.id}`, 'Manage this scope', 'Admin'),
          },
          {
            children: 'Delivery',
            type: 'Navigation' as const,
            to: '/admin/delivery',
            onSelect: () => trackAndNavigate('/admin/delivery', 'Delivery', 'Admin'),
          },
          {
            children: 'Snooze Alarm',
            type: 'Navigation' as const,
            to: '/admin/snooze-alarm',
            onSelect: () => trackAndNavigate('/admin/snooze-alarm', 'Snooze Alarm', 'Admin'),
          },
        ],
      },
    ]
  }, [user.isAdmin, currentScope.id, trackAndNavigate])

  const settings = useMemo(() => {
    if (!canAccessRoute('/settings')) return []
    return [
      {
        heading: 'Settings',
        id: 'settings',
        items: [
          {
            children: 'Settings',
            type: 'Navigation' as const,
            to: '/settings',
            onSelect: () => trackAndNavigate('/settings', 'Settings', 'Settings'),
          },
          {
            children: 'Members',
            type: 'Navigation' as const,
            to: '/settings/members',
            onSelect: () => trackAndNavigate('/settings/members', 'Members', 'Settings'),
          },
          {
            children: 'Sites',
            type: 'Navigation' as const,
            to: '/settings/sites',
            onSelect: () => trackAndNavigate('/settings/sites', 'Sites', 'Settings'),
          },
          {
            children: 'Profile',
            type: 'Navigation' as const,
            to: '/settings/profile',
            onSelect: () => trackAndNavigate('/settings/profile', 'Profile', 'Settings'),
          },
          {
            children: 'Security',
            type: 'Navigation' as const,
            to: '/settings/security',
            onSelect: () => trackAndNavigate('/settings/security', 'Security', 'Settings'),
          },
          {
            children: 'Refresh Groups',
            type: 'Navigation' as const,
            to: '/settings/refresh-groups',
            onSelect: () => trackAndNavigate('/settings/refresh-groups', 'Refresh Groups', 'Settings'),
          },
          {
            children: 'Refresh exceptions',
            type: 'Navigation' as const,
            to: '/settings/refresh-exceptions',
            onSelect: () => trackAndNavigate('/settings/refresh-exceptions', 'Refresh exceptions', 'Settings'),
          },
          {
            children: 'AB test',
            type: 'Navigation' as const,
            to: '/settings/ab-test',
            onSelect: () => trackAndNavigate('/settings/ab-test', 'AB test', 'Settings'),
          },
        ],
      },
    ]
  }, [canAccessRoute, trackAndNavigate])

  const sections = useMemo(
    () =>
      rawSections
        .map((section) => {
          return {
            heading: section.title,
            id: section.title,
            items: section.items
              .flatMap((item) => {
                if (item.type === 'group') {
                  return item.items
                    .filter((i) => i.show ?? true)
                    .map((i) => ({
                      children: i.name,
                      to: i.to,
                      type: 'Navigation' as const,
                      onSelect: () => trackAndNavigate(i.to, i.name, section.title),
                    }))
                } else if (item.show ?? true) {
                  return {
                    children: item.name,
                    type: 'Navigation' as const,
                    to: item.to,
                    onSelect: () => trackAndNavigate(item.to, item.name, section.title),
                  }
                } else {
                  return []
                }
              })
              .filter((i) => canAccessRoute(i.to)),
          }
        })
        .filter((s) => s.items.length),
    [rawSections, canAccessRoute, trackAndNavigate]
  )

  const allItems = useMemo(() => [...sections, ...settings, ...admin], [sections, settings, admin])

  const actions = useMemo(
    () => [
      {
        id: 'copy-scope-id',
        children: 'Copy scopeId',
        type: 'Action',
        onSelect: () =>
          trackAction('copy-scope-id', 'Copy scopeId', () => {
            copyToClipboard(currentScope.id)
            setOpen(false)
          }),
      },
    ],
    [currentScope.id, trackAction]
  )

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
        setPages([])
      }
    }

    document.addEventListener('keydown', down)
    return () => {
      document.removeEventListener('keydown', down)
    }
  }, [])

  const handleSearchChange = (value: string) => {
    setSearch(value)
    if (page === 'searchSite') {
      setSiteSearch(value)
    }
  }

  return (
    <div className="dark">
      <Command.Dialog
        onKeyDown={(e) => {
          // Escape goes to previous page
          // Backspace goes to previous page when search is empty
          if (e.key === 'Escape' || (e.key === 'Backspace' && !search)) {
            e.preventDefault()
            setPages((pages) => pages.slice(0, -1))
            setSiteSearch('')
          }
          if (e.key === 'c' && (e.metaKey || e.ctrlKey) && ['searchSite', 'searchScopes'].includes(page)) {
            e.preventDefault()
            const selectedElement = document.querySelector('[cmdk-item][data-selected="true"]')
            if (selectedElement) {
              const value = selectedElement.getAttribute('data-value')

              let idToCopy = value
              if (page === 'searchScopes') {
                idToCopy = value?.split(':')[1] || ''
              }

              if (idToCopy) {
                posthog?.capture(`command_menu_copy_id`, { command_menu_copy_type: page.replace('search', '').toLowerCase() })
                copyToClipboard(idToCopy)
                // Show copy feedback animation
                setCopiedId(idToCopy)
                setTimeout(() => setCopiedId(null), 600)
              }
            }
          }
        }}
        open={open}
        onOpenChange={(opened) => {
          setOpen(opened)
          if (!opened) {
            setPages([])
            setSearch('')
            setSiteSearch('')
          }
        }}
        label="Global Command Menu"
        filter={(value, search) => {
          if (page === 'searchSite') return 1
          if (value.toLowerCase().includes(search.toLowerCase())) return 1
          return 0
        }}
      >
        <Command.Input value={search} onValueChange={handleSearchChange} placeholder={SEARCH_PLACEHOLDERS[page]} />

        {/* Breadcrumb navigation */}
        {page && (
          <div className="cmdk-breadcrumb">
            <span
              className="cmdk-breadcrumb-item cmdk-breadcrumb-home"
              onClick={() => {
                setPages([])
                setSearch('')
                setSiteSearch('')
              }}
            >
              Home
            </span>
            <Icon name="chevron_right" width="12px" />
            <span className="cmdk-breadcrumb-item cmdk-breadcrumb-current">{PAGE_NAMES[page] || page}</span>
          </div>
        )}

        {page === 'searchSite' ? (
          <Command.List>
            <SitesList search={siteSearch} copiedId={copiedId} onSelectSite={() => setOpen(false)} />
          </Command.List>
        ) : page === 'searchScopes' ? (
          <Command.List>
            <ScopesList onSelectScope={() => setOpen(false)} copiedId={copiedId} />
          </Command.List>
        ) : (
          <Command.List>
            <Command.Empty>No results found.</Command.Empty>
            {!page && (
              <>
                {/* Recent items section */}
                {recentItems.length > 0 && !search && (
                  <Command.Group
                    heading={
                      <span className="cmdk-group-heading-with-icon">
                        <Icon name="time" width="14px" />
                        Recent
                      </span>
                    }
                  >
                    {recentItems.map((item) => (
                      <Command.Item key={`recent-${item.id}`} value={`recent-${item.label}`} onSelect={() => handleRecentItemClick(item)}>
                        <Icon name={item.type === 'navigation' ? 'arrow_right_alt' : (ACTION_ICONS[item.actionId] ?? 'bolt')} width="16px" />
                        {item.label}
                        <span cmdk-meta="">{item.type === 'navigation' ? item.section : 'Action'}</span>
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {/* Admin search items */}
                {user.isAdmin ? (
                  <Command.Group
                    heading={
                      <span className="cmdk-group-heading-with-icon">
                        <Icon name="search" width="14px" />
                        Search
                      </span>
                    }
                  >
                    <Command.Item
                      data-tracking={`command-palette-search-sites`}
                      onSelect={() =>
                        trackAction('search-site', 'Search site…', () => {
                          setPages([...pages, 'searchSite'])
                          setSearch('')
                        })
                      }
                    >
                      <Icon name="site" width="16px" />
                      Search site…
                    </Command.Item>

                    <Command.Item
                      data-tracking={`command-palette-search-scopes`}
                      onSelect={() =>
                        trackAction('search-scope', 'Search scope…', () => {
                          setPages([...pages, 'searchScopes'])
                          setSearch('')
                        })
                      }
                    >
                      <Icon name="folder" width="16px" />
                      Search scope…
                    </Command.Item>
                  </Command.Group>
                ) : null}

                {/* Navigation sections */}
                {allItems.map((section) => (
                  <Command.Group
                    heading={
                      <span className="cmdk-group-heading-with-icon">
                        {SECTION_ICONS[section.heading] && <Icon name={SECTION_ICONS[section.heading]} width="14px" />}
                        {section.heading}
                      </span>
                    }
                    key={section.heading}
                  >
                    {section.items.map((item) => {
                      return (
                        <Command.Item
                          data-tracking={`command-palette-${section.heading}-${item.children}`}
                          value={`${section.heading}-${item.children}`}
                          key={`${section.heading}-${item.children}`}
                          onSelect={item.onSelect}
                        >
                          <Icon name="arrow_right_alt" width="16px" />
                          {item.children}
                        </Command.Item>
                      )
                    })}
                  </Command.Group>
                ))}

                {/* Actions section */}
                <Command.Group
                  heading={
                    <span className="cmdk-group-heading-with-icon">
                      <Icon name="bolt" width="14px" />
                      Actions
                    </span>
                  }
                >
                  {actions.map((action) => {
                    return (
                      <Command.Item value={action.children} key={action.children} onSelect={action.onSelect}>
                        <Icon name={ACTION_ICONS[action.id] ?? 'bolt'} width="16px" />
                        {action.children}
                      </Command.Item>
                    )
                  })}
                </Command.Group>
              </>
            )}
          </Command.List>
        )}

        {/* Footer with keyboard shortcuts */}
        <div className="cmdk-footer">
          <span>
            <kbd>↑↓</kbd> Navigate
          </span>
          <span>
            <kbd>↵</kbd> Select
          </span>
          {page && (
            <span>
              <kbd>⌘C</kbd> Copy ID
            </span>
          )}
          <span>
            <kbd>ESC</kbd> {page ? 'Back' : 'Close'}
          </span>
        </div>
      </Command.Dialog>
    </div>
  )
}

const ScopesList: FC<{ onSelectScope: () => void; copiedId: string | null }> = ({ onSelectScope, copiedId }) => {
  const { data: scopes } = useAdminScopes(true)
  const switchScope = useSwitchScope(onSelectScope)

  if (!scopes.length) return null

  return scopes.map((s) => (
    <Command.Item key={s.id} value={`${s.name}:${s.id}`} onSelect={(value) => switchScope(value.split(':')[1])} className={copiedId === s.id ? 'copied' : ''}>
      {s?.name}{' '}
      <span cmdk-meta="" className="copyable-id">
        {s.id}
      </span>
    </Command.Item>
  ))
}

const SitesList: FC<{ search: string | undefined; copiedId: string | null; onSelectSite: () => void }> = ({ search, copiedId, onSelectSite }) => {
  const { data: sites, isFetching } = useAllAdminSites(search)
  const { data: scopes } = useAdminScopes(true)
  const switchScope = useSwitchScope(onSelectSite)

  const scopeMap = useMemo(() => new Map(scopes.map((s) => [s.id, s.name])), [scopes])

  if (isFetching)
    return (
      <Command.Loading>
        <Skeleton width="100%" bigger={true} />
      </Command.Loading>
    )

  if (!sites.length) return <Command.Empty>No results found.</Command.Empty>

  return sites.map((tag) => {
    const scopeName = scopeMap.get(tag.scopeId)
    return (
      <Command.Item key={tag.id} value={tag.id} onSelect={() => switchScope(tag.scopeId)} className={copiedId === tag.id ? 'copied' : ''}>
        {tag.name}
        {scopeName && ` (${scopeName})`}{' '}
        <span cmdk-meta="" className="copyable-id">
          {tag.id}
        </span>
      </Command.Item>
    )
  })
}
